<template>
  <v-card class="my-4 main">
    <!-- Header -->
    <v-card-title class="cardTitle border-b-thin border-b-md pa-0 pr-4">
      <v-card-actions>
        <v-icon :color="areasValid ? 'green' : '#FB8C00'" class="ma-2">
          {{ areasValid ? "mdi-check-circle" : "mdi-alert" }}
        </v-icon>
        <div>
          Areas
          {{ selectedAreasLength ? `(${selectedAreasLength})` : "" }}
        </div>
      </v-card-actions>
    </v-card-title>
    <v-card-text v-if="!selectedAreasLength" class="text-left pa-4 pb-0">
      <!-- Alert -->
      <v-alert color="info" icon="mdi-information">
        <div style="font-weight: 500">No areas defined</div>
        <div>Add at least one area to filter the indicator data by</div>
      </v-alert>
    </v-card-text>
    <v-card-text
      v-else
      class="pa-0"
      style="max-height: 500px; overflow-y: auto"
    >
      <v-alert
        v-if="OA_sum > 100000"
        color="warning"
        icon="mdi-information"
        class="text-left ma-4"
      >
        <div style="font-weight: 500">Output Area limit exceeded</div>
        <div>
          Your query includes data for {{ OA_sum }} Output Areas, the maximum
          allowed is 100,000. Please adjust your area selection to proceed.
        </div>
      </v-alert>
      <div v-for="(type, index) in selectedAreas" :key="index">
        <v-card-text
          v-for="area in type"
          :key="area.id"
          class="d-flex px-4 py-2 text-left border-b-thin align-center"
          style="max-height: 400px; overflow-y: auto"
        >
          <div>
            <div class="text-body-2 font-weight-regular">
              {{ area.area_name ?? area.name }}
            </div>
            <div class="text-caption font-weight-light">
              {{
                area.category_name ||
                area.name ||
                area.area_code ||
                "Uncategorised"
              }}
              • {{ area.oa_count }} Output Areas included
            </div>
          </div>
          <v-spacer />
          <div>
            <v-btn
              icon="mdi-delete"
              @click="
                areaToRemove = area;
                removeAreaDialog = true;
              "
              color="error"
              :disabled="fetchingCustomAreaOAs || validatingAreaCodes"
              variant="text"
              size="24"
            />
          </div>
        </v-card-text>
      </div>
    </v-card-text>
    <v-card-actions class="pa-4 pt-0">
      <v-btn
        v-if="selectedAreasLength"
        class="mt-4 remove-all-areas-btn"
        variant="text"
        rounded="0"
        prepend-icon="mdi-delete"
        color="error"
        :disabled="fetchingCustomAreaOAs || validatingAreaCodes"
        @click="removeAllAreas"
      >
        remove all areas
      </v-btn>
      <v-spacer />
      <v-btn
        class="mt-4"
        variant="text"
        color="#0e5b99"
        @click="importAreaCodesDialog = true"
        rounded="0"
      >
        import area codes
      </v-btn>
      <v-btn
        class="mt-4 create-api-key-btn"
        variant="elevated"
        prepend-icon="mdi-plus"
        :disabled="validatingAreaCodes"
        color="#0e5b99"
        @click="customAreaSelectorDialog = true"
        rounded="0"
      >
        add area
      </v-btn>
    </v-card-actions>

    <CustomAreaSelector
      v-model:dialog="customAreaSelectorDialog"
      @selectedCustomAreas="editCustomAreas"
      :customAreasProp="selectedCustomAreasIDs"
      okBtnText="update selection"
      :okBtnSpinner="fetchingCustomAreaOAs"
    />

    <DynamicDialog
      :show="removeAreaDialog"
      @dialogOk="removeArea()"
      @dialogCancel="
        removeAreaDialog = false;
        areaToRemove = null;
      "
      @update:showDyamicDialog="(state) => (removeAreaDialog = state)"
      max-width="420"
      cancelBtnColor="none"
      minHeight="100px"
      okBtnColor="error"
      cancelBtnVariant="text"
    >
      <template v-slot:title>Remove area</template>
      <template v-slot:content>
        <v-card-actions class="pa-0 text-center align-center">
          Are you sure you want to remove this area from the query
        </v-card-actions>
      </template>
      <template v-slot:okBtnTitle>remove</template>
    </DynamicDialog>
    <DynamicDialog
      :show="removeAllAreasDialog"
      @dialogOk="confirmRemoveAllAreas"
      @dialogCancel="removeAllAreasDialog = false"
      @update:showDyamicDialog="(state) => (removeAllAreasDialog = state)"
      max-width="420"
      cancelBtnColor="none"
      minHeight="100px"
      okBtnColor="error"
      cancelBtnVariant="text"
    >
      <template v-slot:title>Remove all areas</template>
      <template v-slot:content>
        <v-card-actions class="pa-0 text-center align-center">
          This will remove all currently selected areas. Are you sure you want
          to continue?
        </v-card-actions>
      </template>
      <template v-slot:okBtnTitle>remove all</template>
    </DynamicDialog>

    <DynamicDialog
      :show="importAreaCodesDialog"
      @dialogOk="validateAndImportAreaCodes"
      @dialogCancel="importAreaCodesDialog = false"
      @update:showDyamicDialog="(state) => (importAreaCodesDialog = state)"
      maxWidth="700"
      :okBtnSpinner="validatingAreaCodes"
      :okBtnDisabled="!areaCodesImport"
      cancelBtnColor="none"
      okBtnColor="success"
      cancelBtnVariant="text"
    >
      <template v-slot:title>Import Area Codes</template>
      <template v-slot:content>
        <div v-if="!selectedIndicator">
          <v-alert color="warning">
            <v-row>
              <v-col cols="8" style="line-height: 1.3">
                <div>
                  <b>Warning</b>
                </div>
                <div>
                  Please select an indicator before importing area codes. The
                  available area codes depend on the countries covered by the
                  chosen indicator. Once an indicator is selected, you will see
                  which area code prefixes are valid for import.
                </div>
              </v-col>
              <v-col cols="4" class="d-flex justify-center align-center">
                <v-btn
                  variant="text"
                  color="warning"
                  rounded="0"
                  @click="
                    importAreaCodesDialog = false;
                    $router.push('/datastore/catalogue');
                  "
                >
                  select indicator
                </v-btn>
              </v-col>
            </v-row>
          </v-alert>
        </div>
        <div v-else>
          You can only import area codes from the countries covered by the
          selected indicator. The valid prefixes below correspond to the
          indicator's coverage:
          <ul>
            <li v-if="selectedIndicator.eng">
              England: E00, E01, E02, E05, E06, E07, E08, E09, E10, E12
            </li>
            <li v-if="selectedIndicator.sct">
              Scotland: S00, S01, S02, S12, S13, S14
            </li>
            <li v-if="selectedIndicator.wls">
              Wales: W00, W01, W02, W05, W06, W07
            </li>
          </ul>
          <div class="mt-3">
            Enter your desired area codes below. Separate each code with a comma
            or place each one on a new line.
          </div>
          <div class="mt-3">
            <v-textarea
              v-model="areaCodesImport"
              @keydown.enter="
                (e) => {
                  if (e.ctrlKey) validateAndImportAreaCodes();
                }
              "
              variant="outlined"
              row-height="30"
              rows="8"
              density="compact"
              rounded="0"
              autocomplete="off"
              :placeholder="importStringExample"
            ></v-textarea>
          </div>
          <div v-if="!validatingAreaCodes && invalidAreaCodes.length">
            <v-alert color="warning">
              <div>
                <b>Some area codes couldn't be recognised</b>
              </div>
              <div>
                None of the area codes have been added. The entries listed below
                are either invalid or fall outside the selected indicator's
                coverage. Please review the errors, make the necessary
                amendments, and try importing again.
              </div>
              <div
                style="
                  background: white;
                  color: black;
                  max-height: 200px;
                  overflow-y: auto;
                "
                class="pa-4"
              >
                <div
                  v-for="(invalidCode, index) in invalidAreaCodes"
                  :key="index"
                >
                  {{ invalidCode }}
                </div>
              </div>
            </v-alert>
          </div>
        </div>
      </template>
      <template v-slot:okBtnTitle>validate & import</template>
    </DynamicDialog>
  </v-card>
</template>

<script>
import CustomAreaSelector from "@/components/CustomAreaSelector.vue";
import DynamicDialog from "@/components/DynamicDialog.vue";
import { systemMessages } from "@/mixins/SystemMessages";

export default {
  name: "DatastoreQueryBuilderAreas",
  mixins: [systemMessages],
  data: () => ({
    customAreaSelectorDialog: false,
    fetchingCustomAreaOAs: false,
    removeAreaDialog: false,
    removeAllAreasDialog: false,
    importAreaCodesDialog: false,
    areaToRemove: null,
    areasInSelector: [], // picked areas in the selector. separate from selectedAreas to avoid reactivity issues
    invalidAreaCodes: [],
    areaCodesImport: "",
    validatingAreaCodes: false,
  }),
  props: {
    areas: {
      type: Object,
      default: () => ({
        customAreas: [],
        areaCodes: [],
      }),
      required: true,
    },
    selectedAreasLength: {
      type: Number,
      default: 0,
      required: false,
    },
  },
  components: {
    CustomAreaSelector,
    DynamicDialog,
  },
  computed: {
    selectedAreas: {
      get() {
        return this.areas;
      },
      set(val) {
        this.$emit("update:areas", val);
      },
    },
    importStringExample() {
      if (!this.selectedIndicator)
        return "Select an indicator to see an example";
      const locales = {
        eng: ["E01000001", "E01000002"],
        sct: ["S00088956", "S00088957"],
        wls: ["W00000001", "W00000002"],
      };

      let example = "Example:";
      Object.entries(locales).forEach(([country, codes]) => {
        if (this.selectedIndicator[country]) {
          codes.forEach((code) => {
            example += `\n${code}`;
          });
        }
      });

      return example;
    },
    selectedCustomAreasIDs() {
      return this.selectedAreas.customAreas.map((area) => area.id);
    },
    areasValid() {
      return this.selectedAreasLength && this.OA_sum <= 100000;
    },
    OA_sum() {
      let sum = 0;

      if (this.selectedAreasLength) {
        Object.values(this.selectedAreas).forEach((type) => {
          type.forEach((area) => {
            sum += area.oa_count;
          });
        });
      }

      return sum;
    },
    selectedIndicator: {
      get() {
        return this.$store.getters["datastore/selectedIndicator"];
      },
      set(val) {
        this.$store.commit("datastore/setSelectedIndicator", val);
      },
    },
  },
  methods: {
    async editCustomAreas(selectedAreas) {
      try {
        this.fetchingCustomAreaOAs = true;

        const response = await this.$axios.post(
          "/datastore/query-builder/custom-areas-oas",
          { customAreaIDs: selectedAreas },
        );

        // add custom areas
        this.selectedAreas.customAreas = response.data;

        this.customAreaSelectorDialog = false;
      } catch {
        this.errorPill({
          title: "Failed to fetch custom areas. Try later",
          timeout: 3000,
        });
      } finally {
        this.fetchingCustomAreaOAs = false;
      }
    },
    removeArea() {
      const type = this.areaToRemove.category_name
        ? "customAreas"
        : "areaCodes";

      const uniqueKey = type === "customAreas" ? "id" : "area_code";

      this.selectedAreas[type] = this.selectedAreas[type].filter(
        (area) => area[uniqueKey] !== this.areaToRemove[uniqueKey],
      );
      this.removeAreaDialog = false;
    },
    removeAllAreas() {
      this.removeAllAreasDialog = true; // Show the dialog
    },
    confirmRemoveAllAreas() {
      this.selectedAreas = {
        customAreas: [],
        areaCodes: [],
      };
      this.removeAllAreasDialog = false; // Close the dialog
    },
    validateAndImportAreaCodes() {
      if (!this.selectedIndicator) return;

      const areaCodes = this.areaCodesImport
        .split(/[\n,]/)
        .map((code) => code.trim())
        .filter((code) => code);

      if (areaCodes.length > 100000) {
        this.warningPill({
          title:
            "You can only import a maximum of 100,000 area codes at a time",
          timeout: 5000,
        });
        return;
      }

      // check that area codes start with corresponding prefix of the selected indicator e.g: E, W, S
      const validPrefixes = {
        eng: ["E"],
        sct: ["S"],
        wls: ["W"],
      };

      //performs frontend and backend validations and then shows errors for both at once
      this.validatingAreaCodes = true;

      // Find invalid areaCodes based on selected indicator coverage vs client country
      let regex = Object.entries(validPrefixes)
        .filter(([key]) => this.selectedIndicator[key])
        .map(([, prefixes]) => `^(${prefixes.join("|")})`)
        .join("|");
      this.invalidAreaCodes = areaCodes.filter((code) => {
        return !new RegExp(regex).test(code);
      });

      this.$axios
        .post("/datastore/query-builder/area-codes", {
          areaCodes: areaCodes,
        })
        .then((response) => {
          // if there are any invalid area codes based just on indicator coverage - stop
          if (this.invalidAreaCodes.length) {
            return; //validation message already shown
          }

          // add area codes
          this.selectedAreas.areaCodes = [
            ...(this.selectedAreas.areaCodes || []),
            ...response.data,
          ];

          // remove duplicates
          this.selectedAreas.areaCodes = [
            ...new Map(
              this.selectedAreas.areaCodes.map((area) => [
                area.area_code,
                area,
              ]),
            ).values(),
          ];

          // clean after ourself
          this.importAreaCodesDialog = false;
          this.areaCodesImport = "";
        })
        .catch((error) => {
          if (error.response.data.invalidCodes) {
            //show any frontend and backend area code validation errors
            this.invalidAreaCodes = [
              ...this.invalidAreaCodes,
              ...error.response.data.invalidCodes,
            ];
          } else {
            this.errorPill({
              title: error.response.data.message,
              timeout: 5000,
            });
          }
        })
        .finally(() => {
          this.validatingAreaCodes = false;
        });
    },
  },
};
</script>
<style scoped>
/* Add your styles here */
</style>
